import * as React from 'react';
import { useState, useEffect } from 'react';
import { Employee } from '../../../hooks/useEmployeeData';
import { DateRange } from './DateRangeFilter';
import { WorkArea, TimeTracking, PreviewSummary, ensureExcelJS } from './ttImportUtils';
import { TTImportUploadStep } from './TTImportUploadStep';
import { TTImportMappingStep } from './TTImportMappingStep';
import { TTImportPreviewStep } from './TTImportPreviewStep';
import { TTImportExecuteStep } from './TTImportExecuteStep';
import css from './TTImportWizard.module.css';

interface TTImportWizardProps {
  open: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  employees: Employee[];
  workAreas: WorkArea[];
  activeEmployeeId?: number | null;
  activeEmployeeName?: string | null;
  currentRangeStr: { from: string; to: string };
  dateRange: DateRange | null;
  selectedWAs: number[];
  loadTimeTrackings: (range: DateRange, workAreaIds: number[], employeeId?: number | null) => Promise<void>;
  setTimeTrackings: React.Dispatch<React.SetStateAction<TimeTracking[]>>;
  setApprovedCrMap: React.Dispatch<React.SetStateAction<Record<number, { start: string; end: string }>>>;
  setPendingCrMap: React.Dispatch<React.SetStateAction<Record<number, { start: string; end: string }>>>;
  setRestoredOriginalMap: React.Dispatch<React.SetStateAction<Record<number, { start: string; end: string }>>>;
  markCrPresence: (ttId: number, has: boolean) => void;
  uiLedger: Record<number, { originalStart: string; originalEnd: string }>;
  persistLedger: (next: Record<number, { originalStart: string; originalEnd: string }>) => void;
  setToast: React.Dispatch<React.SetStateAction<{ type: 'success' | 'error' | 'info'; text: string } | null>>;
  setFileInputKey: React.Dispatch<React.SetStateAction<number>>;
}

export default function TTImportWizard({
  open,
  onClose,
  onImportComplete,
  employees,
  workAreas,
  activeEmployeeId,
  activeEmployeeName,
  currentRangeStr,
  dateRange,
  selectedWAs,
  loadTimeTrackings,
  setTimeTrackings,
  setApprovedCrMap,
  setPendingCrMap,
  setRestoredOriginalMap,
  markCrPresence,
  uiLedger,
  persistLedger,
  setToast,
  setFileInputKey,
}: TTImportWizardProps): React.JSX.Element | null {
  const [importFile, setImportFile] = useState<File | null>(null);
  const importInputRef = React.useRef<HTMLInputElement>(null);
  const [importStep, setImportStep] = useState<number>(1);
  const [importParsing, setImportParsing] = useState<boolean>(false);
  const [importError, setImportError] = useState<string>('');
  const [importHeader, setImportHeader] = useState<string[]>([]);
  const [importRows, setImportRows] = useState<any[]>([]);
  const [importMapping, setImportMapping] = useState<Record<string, string>>({});
  const [importSamples, setImportSamples] = useState<number>(0);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string>('');
  const [previewRows, setPreviewRows] = useState<any[]>([]);
  const [previewSummary, setPreviewSummary] = useState<PreviewSummary>({
    okNew: 0, okCr: 0, warn: 0, error: 0, skipped: 0,
  });
  const [existingTTs, setExistingTTs] = useState<TimeTracking[]>([]);
  const [importRunning, setImportRunning] = useState<boolean>(false);
  const [importProgress, setImportProgress] = useState<{ done: number; total: number }>({
    done: 0, total: 0,
  });
  const [importLog, setImportLog] = useState<string[]>([]);
  const [importResult, setImportResult] = useState<{
    created: number; cr: number; errors: number;
  }>({ created: 0, cr: 0, errors: 0 });
  const [importCompleted, setImportCompleted] = useState<boolean>(false);
  const [fallbackEnabled, setFallbackEnabled] = useState<boolean>(true);
  const [ttFormBusy, setTtFormBusy] = useState<boolean>(false);
  const [localFileInputKey, setLocalFileInputKey] = useState<number>(0);

  useEffect(() => {
    if (open) { ensureExcelJS().catch(() => {}); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!importCompleted) return;
    if (importResult.errors === 0) {
      const created = importResult.created;
      const cr = importResult.cr;
      onClose();
      setImportStep(1);
      setImportFile(null);
      setImportRows([]);
      setImportHeader([]);
      setImportMapping({});
      setImportSamples(0);
      setPreviewRows([]);
      setPreviewSummary({ okNew: 0, okCr: 0, warn: 0, error: 0, skipped: 0 });
      setImportCompleted(false);
      setImportRunning(false);
      setFileInputKey((k) => k + 1);
      setLocalFileInputKey((k) => k + 1);
      setToast({
        type: 'success',
        text: `Import erfolgreich: ${created} neue Eintr\u00e4ge, ${cr} Korrekturen.`,
      });
    }
  }, [open, importCompleted, importResult]);

  if (!open) return null;

  return (
    <div className={css.overlay} onClick={onClose} data-testid="tt-import-modal">
      <div className={css.modal} onClick={(e) => e.stopPropagation()}>
        <h3 className={css.modalTitle}>Time Trackings &ndash; Form &amp; Import</h3>
        <div className={css.stepContainer}>
          <TTImportUploadStep
            employees={employees}
            workAreas={workAreas}
            activeEmployeeId={activeEmployeeId}
            activeEmployeeName={activeEmployeeName}
            importFile={importFile}
            importParsing={importParsing}
            ttFormBusy={ttFormBusy}
            localFileInputKey={localFileInputKey}
            importInputRef={importInputRef}
            setImportFile={setImportFile}
            setImportParsing={setImportParsing}
            setImportError={setImportError}
            setImportHeader={setImportHeader}
            setImportRows={setImportRows}
            setImportMapping={setImportMapping}
            setImportSamples={setImportSamples}
            setImportStep={setImportStep}
            setPreviewLoading={setPreviewLoading}
            setPreviewError={setPreviewError}
            setPreviewRows={setPreviewRows}
            setPreviewSummary={setPreviewSummary}
            setTtFormBusy={setTtFormBusy}
          >
            {importStep === 2 && (
              <TTImportMappingStep
                importHeader={importHeader}
                importSamples={importSamples}
                importRows={importRows}
                setImportStep={setImportStep}
              />
            )}

            {importStep >= 3 && (
              <TTImportPreviewStep
                open={open}
                importStep={importStep}
                importRows={importRows}
                importMapping={importMapping}
                importSamples={importSamples}
                workAreas={workAreas}
                previewLoading={previewLoading}
                previewError={previewError}
                previewRows={previewRows}
                previewSummary={previewSummary}
                setPreviewLoading={setPreviewLoading}
                setPreviewError={setPreviewError}
                setPreviewRows={setPreviewRows}
                setPreviewSummary={setPreviewSummary}
                setExistingTTs={setExistingTTs}
                setImportStep={setImportStep}
              />
            )}

            {(importStep === 4 || importStep === 5) && (
              <TTImportExecuteStep
                importStep={importStep}
                previewRows={previewRows}
                fallbackEnabled={fallbackEnabled}
                importRunning={importRunning}
                importCompleted={importCompleted}
                importProgress={importProgress}
                importLog={importLog}
                importResult={importResult}
                importFile={importFile}
                activeEmployeeId={activeEmployeeId}
                dateRange={dateRange}
                selectedWAs={selectedWAs}
                uiLedger={uiLedger}
                setImportStep={setImportStep}
                setFallbackEnabled={setFallbackEnabled}
                setImportRunning={setImportRunning}
                setImportProgress={setImportProgress}
                setImportLog={setImportLog}
                setImportResult={setImportResult}
                setImportCompleted={setImportCompleted}
                setTimeTrackings={setTimeTrackings}
                setApprovedCrMap={setApprovedCrMap}
                setPendingCrMap={setPendingCrMap}
                setRestoredOriginalMap={setRestoredOriginalMap}
                markCrPresence={markCrPresence}
                persistLedger={persistLedger}
                setToast={setToast}
                loadTimeTrackings={loadTimeTrackings}
                onClose={onClose}
                setFileInputKey={setFileInputKey}
                setLocalFileInputKey={setLocalFileInputKey}
              />
            )}

            {importError && <div className={css.errorBanner}>{importError}</div>}
          </TTImportUploadStep>
        </div>
        <div className={css.footerRight}>
          <button className={css.btnClose} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
